import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BudgetCard } from './budget-card';
import { BudgetTable } from './budget-table';
import { BudgetProgressBar } from './budget-progress-bar';
import { RateCardSheet } from '@/components/RateCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, CheckCircle2, Circle, Clock, Coins, Plus, History, Download, Edit, Receipt } from 'lucide-react';
import { formatCurrency, formatPercent, formatHours } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import type { SalesBudget } from '@/types/budget.types';
import type { ProjectType } from '@/types';

interface SalesBudgetViewProps {
  projectId: number;
  data?: SalesBudget;
  costBudget?: any; // Cost budget data to get categories from
  projectType: ProjectType;
  onCreateBudget?: () => void;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
}

export function SalesBudgetView({ projectId, data, costBudget, projectType, onCreateBudget, onEdit, onExport, onHistory }: SalesBudgetViewProps) {
  // Check if data is missing or incomplete for the project type
  const isDataIncomplete = !data || 
    (projectType === 'fixed-price' && (!data.contractValue || !data.milestones || !data.revenueRecognition || !data.phases)) ||
    (projectType === 'time-and-materials' && !data.categories?.length && !data.estimatedRevenue) ||
    (projectType === 'retainer' && !data.monthlyRetainer && !data.retainer);

  if (isDataIncomplete) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Coins className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Sales Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Track revenue, milestones, and billing by creating a sales budget for this project.
            </p>
            {onCreateBudget && (
              <Button onClick={onCreateBudget}>
                <Plus className="mr-2 h-4 w-4" />
                Create Sales Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (projectType === 'fixed-price') {
    return <FixedPriceSalesView projectId={projectId} data={data} onEdit={onEdit} onExport={onExport} onHistory={onHistory} />;
  } else if (projectType === 'time-and-materials') {
    return <TimeAndMaterialsSalesView projectId={projectId} data={data} costBudget={costBudget} onEdit={onEdit} onExport={onExport} onHistory={onHistory} />;
  } else if (projectType === 'retainer') {
    return <RetainerSalesView projectId={projectId} data={data} onEdit={onEdit} onExport={onExport} onHistory={onHistory} />;
  }

  return <div>Unknown project type</div>;
}

// Fixed Price Project View
function FixedPriceSalesView({ projectId, data, onEdit, onExport, onHistory }: { projectId?: number; data: SalesBudget; onEdit?: () => void; onExport?: () => void; onHistory?: () => void }) {
  const { contractValue = 0, milestones = [] } = data;
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
      <CardContent className="space-y-6">
        {/* One-line Summary */}
        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-sm text-neutral-600">Budget:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(contractValue)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Invoiced:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(milestones.filter(m => m.invoiced).reduce((sum, m) => sum + m.amount, 0))}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Paid:</span>
              <span className="ml-2 text-base font-semibold text-success-600">
                {formatCurrency(milestones.filter(m => m.paid).reduce((sum, m) => sum + m.amount, 0))}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Outstanding:</span>
              <span className="ml-2 text-base font-semibold text-warning-600">
                {formatCurrency(milestones.filter(m => m.invoiced && !m.paid).reduce((sum, m) => sum + m.amount, 0))}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Schedule Table */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Payment Schedule</h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold">Name</TableHead>
                  <TableHead className="font-bold text-right">Budget</TableHead>
                  <TableHead className="font-bold text-right">Invoiced</TableHead>
                  <TableHead className="font-bold text-right">Paid</TableHead>
                  <TableHead className="font-bold text-right">Variance</TableHead>
                  <TableHead className="font-bold text-right">Due Date</TableHead>
                  <TableHead className="font-bold text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {milestones.map((milestone) => {
                  const invoicedAmount = milestone.invoiced ? milestone.amount : 0;
                  const paidAmount = milestone.paid ? milestone.amount : 0;
                  const variance = milestone.amount - invoicedAmount;
                  
                  // Determine status
                  let status = 'Pending';
                  let statusVariant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' = 'secondary';
                  
                  if (milestone.paid) {
                    status = 'Paid';
                    statusVariant = 'success';
                  } else if (milestone.invoiced) {
                    status = 'Invoiced';
                    statusVariant = 'warning';
                  } else if (new Date(milestone.dueDate) < new Date()) {
                    status = 'Overdue';
                    statusVariant = 'destructive';
                  }

                  return (
                    <TableRow key={milestone.id}>
                      <TableCell className="font-medium">{milestone.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(milestone.amount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(invoicedAmount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(paidAmount)}</TableCell>
                      <TableCell className={cn("text-right tabular-nums font-medium", variance > 0 ? "text-warning-600" : "text-success-600")}>
                        {formatCurrency(variance)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{new Date(milestone.dueDate).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={statusVariant}>{status}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow className="font-bold bg-muted/50 border-t-2">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(contractValue)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(milestones.filter(m => m.invoiced).reduce((sum, m) => sum + m.amount, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(milestones.filter(m => m.paid).reduce((sum, m) => sum + m.amount, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(contractValue - milestones.filter(m => m.invoiced).reduce((sum, m) => sum + m.amount, 0))}
                  </TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">—</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}

// Time & Materials Project View
function TimeAndMaterialsSalesView({ projectId, data, costBudget, onEdit, onExport, onHistory }: { projectId?: number; data: SalesBudget; costBudget?: any; onEdit?: () => void; onExport?: () => void; onHistory?: () => void }) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  // Use categories from API data if available, otherwise fall back to defaults
  // Deduplicate by name, keeping the first occurrence of each category
  const categories = React.useMemo(() => {
    if (data.categories && data.categories.length > 0) {
      const seen = new Set<string>();
      return data.categories
        .map(cat => ({
          id: String(cat.id),
          name: cat.name,
          budget: cat.budget,
          actual: cat.actual,
        }))
        .filter(cat => {
          if (seen.has(cat.name)) return false;
          seen.add(cat.name);
          return true;
        });
    }
    // Fallback default categories for display when no API data exists
    return [
      { id: '1', name: 'Labour', budget: 350000, actual: 231000 },
      { id: '2', name: 'Materials', budget: 104000, actual: 0 },
      { id: '3', name: 'Equipment', budget: 78000, actual: 49400 },
      { id: '4', name: 'Subcontractors', budget: 84000, actual: 0 },
      { id: '5', name: 'Travel & Expenses', budget: 0, actual: 0 },
      { id: '6', name: 'Software & Licenses', budget: 36400, actual: 23400 },
      { id: '7', name: 'Overhead', budget: 0, actual: 0 },
      { id: '8', name: 'Contingency', budget: 19500, actual: 11700 },
    ];
  }, [data.categories]);

  // Calculate totals from categories
  const budget = categories.reduce((sum: number, cat: { budget: number; actual: number }) => sum + cat.budget, 0);
  const actual = categories.reduce((sum: number, cat: { budget: number; actual: number }) => sum + cat.actual, 0);
  const variance = budget - actual; // Budget minus Actual = remaining budget (positive is good)
  const utilizationPercent = budget > 0 ? (actual / budget) * 100 : 0;
  const NEAR_BUDGET_THRESHOLD = 80; // 80% utilization triggers warning

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
      <CardContent className="space-y-6">
        {/* One-line Summary */}
        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-sm text-neutral-600">Budget:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(budget)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Actual:</span>
              <span className="ml-2 text-base font-semibold text-neutral-900">
                {formatCurrency(actual)}
              </span>
            </div>
            <div>
              <span className="text-sm text-neutral-600">Remaining:</span>
              <span className={cn(
                "ml-2 text-base font-semibold",
                variance < 0 
                  ? "text-error-600" // Over budget - red
                  : utilizationPercent >= NEAR_BUDGET_THRESHOLD 
                    ? "text-warning-600" // Nearing budget (80%+) - amber
                    : variance > 0 
                      ? "text-success-600" // Under budget - green
                      : "text-neutral-900" // Exactly on budget
              )}>
                {variance > 0 ? '+' : ''}{formatCurrency(variance)}
              </span>
            </div>
          </div>
        </div>

        {/* Budget Categories Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Category</TableHead>
                <TableHead className="font-bold text-right">Budget</TableHead>
                <TableHead className="font-bold text-right">Actual</TableHead>
                <TableHead className="font-bold text-right">Remaining</TableHead>
                <TableHead className="font-bold text-right">% Billed</TableHead>
                <TableHead className="font-bold text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category: { id: string; name: string; budget: number; actual: number }) => {
                const remaining = category.budget - category.actual;
                const percentBilled = category.budget > 0 ? (category.actual / category.budget) * 100 : 0;
                
                // Status logic based on % billed
                let status: 'success' | 'warning' | 'destructive' = 'success';
                let statusText = 'On Track';
                
                if (percentBilled >= 100) {
                  status = 'destructive';
                  statusText = 'Over Budget';
                } else if (percentBilled >= 80) {
                  status = 'warning';
                  statusText = 'At Risk';
                }

                return (
                  <TableRow key={category.id}>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(category.budget)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(category.actual)}</TableCell>
                    <TableCell className={cn(
                      "text-right tabular-nums font-medium",
                      remaining >= 0 ? "text-success-600" : "text-error-600"
                    )}>
                      {remaining >= 0 ? '+' : ''}{formatCurrency(remaining)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatPercent(percentBilled, 1)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={status} accent={status}>
                        {statusText}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Total Row */}
              <TableRow className="font-bold bg-muted/50 border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(budget)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(actual)}</TableCell>
                <TableCell className={cn(
                  "text-right tabular-nums",
                  variance < 0 
                    ? "text-error-600" 
                    : utilizationPercent >= NEAR_BUDGET_THRESHOLD
                      ? "text-warning-600"
                      : variance > 0 
                        ? "text-success-600" 
                        : "text-neutral-900"
                )}>
                  {variance > 0 ? '+' : ''}{formatCurrency(variance)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatPercent(utilizationPercent, 1)}
                </TableCell>
                <TableCell className="text-right">
                  <Badge 
                    variant={variance < 0 ? 'destructive' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'warning' : 'success'}
                    accent={variance < 0 ? 'destructive' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'warning' : 'success'}
                  >
                    {variance < 0 ? 'Over Budget' : utilizationPercent >= NEAR_BUDGET_THRESHOLD ? 'At Risk' : 'On Track'}
                  </Badge>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}

// Retainer Project View
function RetainerSalesView({ projectId, data, onEdit, onExport, onHistory }: { projectId?: number; data: SalesBudget; onEdit?: () => void; onExport?: () => void; onHistory?: () => void }) {
  const { retainer, utilisation, monthlyBreakdown = [] } = data;
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  const monthlyRows = monthlyBreakdown.map((month, index) => ({
    id: index + 1,
    month: month.month,
    retainer: formatCurrency(month.retainerAmount),
    hoursIncluded: formatHours(month.hoursIncluded),
    hoursUsed: formatHours(month.hoursUsed),
    overage: month.overageAmount > 0 ? formatCurrency(month.overageAmount) : '-',
    total: formatCurrency(month.totalBilled),
  }));

  // Calculate YTD totals
  const ytdTotals = monthlyBreakdown.reduce(
    (acc, month) => ({
      retainer: acc.retainer + month.retainerAmount,
      hoursIncluded: acc.hoursIncluded + month.hoursIncluded,
      hoursUsed: acc.hoursUsed + month.hoursUsed,
      overage: acc.overage + month.overageAmount,
      total: acc.total + month.totalBilled,
    }),
    { retainer: 0, hoursIncluded: 0, hoursUsed: 0, overage: 0, total: 0 }
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Sales Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
        <div className="space-y-6">
          {/* Retainer Summary */}
          {retainer && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Monthly Retainer"
          value={formatCurrency(retainer.monthlyAmount)}
          subtitle="/month"
          variant="default"
        />
        <BudgetCard
          title="Contract Value"
          value={formatCurrency(retainer.contractValue)}
          subtitle={`${retainer.contractMonths} months`}
          variant="default"
        />
        <BudgetCard
          title="Invoiced"
          value={formatCurrency(retainer.invoiced)}
          subtitle={`${retainer.monthsCompleted} months`}
          variant="default"
        />
        <BudgetCard
          title="Remaining"
          value={formatCurrency(retainer.remaining)}
          subtitle={`${retainer.monthsRemaining} months`}
          variant="default"
        />
      </div>
          )}

      {/* Hours Utilisation - Current Month */}
      {utilisation && (
      <Card>
        <CardHeader>
          <CardTitle>Hours Utilisation (Current Month: {utilisation.currentMonth})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Included Hours:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.includedHours)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Used:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursUsed)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Remaining:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursRemaining)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Overage Rate:</div>
                <div className="text-2xl font-bold">{formatCurrency(utilisation.overageRate)}/hr</div>
              </div>
            </div>

            <BudgetProgressBar
              value={utilisation.percentUsed}
              max={100}
              variant="default"
              showLabel
              label={`${formatPercent(utilisation.percentUsed)} used`}
            />

            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">Rollover Policy:</span>
              <div className="flex gap-2">
                <Badge variant={utilisation.rolloverPolicy === 'none' ? 'default' : 'outline'}>
                  No rollover
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'limited' ? 'default' : 'outline'}>
                  Max {utilisation.rolloverHours} hrs
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'unlimited' ? 'default' : 'outline'}>
                  Unlimited
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'month', label: 'Month', align: 'left' },
              { key: 'retainer', label: 'Retainer', align: 'right' },
              { key: 'hoursIncluded', label: 'Hours Incl', align: 'right' },
              { key: 'hoursUsed', label: 'Hours Used', align: 'right' },
              { key: 'overage', label: 'Overage', align: 'right' },
              { key: 'total', label: 'Total', align: 'right' },
            ]}
            data={monthlyRows}
            totalRow={{
              month: 'YTD Total',
              retainer: formatCurrency(ytdTotals.retainer),
              hoursIncluded: formatHours(ytdTotals.hoursIncluded),
              hoursUsed: formatHours(ytdTotals.hoursUsed),
              overage: formatCurrency(ytdTotals.overage),
              total: formatCurrency(ytdTotals.total),
            }}
          />
        </CardContent>
      </Card>
        </div>
      </CardContent>
    </Card>

    {/* Rate Card Sheet */}
    <RateCardSheet
      open={isRateCardOpen}
      onOpenChange={setIsRateCardOpen}
      projectId={projectId?.toString() || '0'}
    />
    </>
  );
}
